import React, { useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import CheckCircleOutlined from '@material-ui/icons/CheckCircleOutlined';
import WarningRounded from '@material-ui/icons/NewReleasesOutlined';
import AppsRounded from '@material-ui/icons/WidgetsOutlined';
import DescriptionOutlined from '@material-ui/icons/DescriptionOutlined';
import LinkOutlined from '@material-ui/icons/LinkOutlined';
import { useNavigate } from 'react-router-dom';
import { useApi, discoveryApiRef, fetchApiRef, alertApiRef } from '@backstage/core-plugin-api';
import { useOnboardingContext } from '../OnboardingContext';
import { getOnboardingColors } from '../colors';

export interface PlaygroundLabels {
  title: string;
  description: string;
  taskListHeader: string;
  tasks: string[];
  tutorialLabel: string;
  tutorialDescription: string;
  tutorialUrl: string;
  repoLabel: string;
  repoUrl: string;
  verifyHint: string;
  verify: string;
  completed: string;
  completedMessage: string;
  nextJourneyUnlocked: string;
  continueToJourney: string;
  failedTitle: string;
  failedMessage: string;
  failedHint: string;
  reviewJourney: string;
  verifyAgain: string;
}

const useStyles = makeStyles(theme => {
  const c = getOnboardingColors(theme);
  return {
    container: {
      textAlign: 'center',
      padding: theme.spacing(2, 0),
    },
    icon: {
      fontSize: 48,
      color: c.primary,
      marginBottom: theme.spacing(1),
    },
    title: {
      fontWeight: 700,
      marginBottom: theme.spacing(1),
    },
    description: {
      fontSize: '0.875rem',
      color: theme.palette.text.secondary,
      margin: theme.spacing(0, 2, 2),
      lineHeight: 1.5,
    },
    taskList: {
      textAlign: 'left',
      backgroundColor: theme.palette.background.default,
      borderRadius: 8,
      padding: theme.spacing(2, 3),
      margin: theme.spacing(0, 2, 2),
    },
    taskListHeader: {
      fontWeight: 700,
      fontSize: '0.875rem',
      marginBottom: theme.spacing(0.5),
    },
    taskItem: {
      fontSize: '0.875rem',
      lineHeight: 1.8,
    },
    linkCard: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1.5),
      border: `2px solid ${c.cardBorder}`,
      borderRadius: 8,
      padding: theme.spacing(1.5, 2),
      marginBottom: theme.spacing(1),
      margin: theme.spacing(0, 3.5, 1),
      textAlign: 'left',
      cursor: 'pointer',
      textDecoration: 'none',
      color: 'inherit',
      boxShadow: '0px 0px 4px 0px #0000001A',
      transition: 'all 0.2s ease',
      '&:hover': {
        opacity: 0.85,
        boxShadow: 'none',
      },
    },
    linkCardIcon: {
      fontSize: 28,
      color: theme.palette.text.secondary,
      flexShrink: 0,
    },
    linkCardTitle: {
      fontWeight: 600,
      fontSize: '1rem',
      lineHeight: '1.25rem',
    },
    linkCardDescription: {
      fontSize: '0.875rem',
      color: theme.palette.text.secondary,
      lineHeight: '1rem',
    },
    helperText: {
      fontSize: '0.8rem',
      color: theme.palette.text.secondary,
      marginBottom: theme.spacing(2),
    },
    verifyButton: {
      fontWeight: 700,
      textTransform: 'none',
      borderRadius: 4,
      padding: theme.spacing(1, 3),
      backgroundColor: c.buttonPrimaryBg,
      color: c.buttonPrimaryFg,
      '&:hover': {
        backgroundColor: c.primaryHover,
      },
    },
    resultIconSuccess: {
      fontSize: 64,
      color: c.success,
      marginBottom: theme.spacing(1),
    },
    resultIconFail: {
      fontSize: 64,
      color: '#e6a817',
      marginBottom: theme.spacing(1),
    },
    resultTitle: {
      fontWeight: 700,
      marginBottom: theme.spacing(1),
    },
    resultMessage: {
      fontSize: '0.875rem',
      color: theme.palette.text.secondary,
      margin: theme.spacing(0, 2, 3),
      lineHeight: 1.5,
    },
    continueButton: {
      fontWeight: 700,
      textTransform: 'none',
      borderRadius: 4,
      padding: theme.spacing(1, 3),
      backgroundColor: c.buttonPrimaryBg,
      color: c.buttonPrimaryFg,
      '&:hover': {
        backgroundColor: c.primaryHover,
      },
    },
    hintBox: {
      backgroundColor: c.warningBannerBg,
      borderRadius: 4,
      padding: theme.spacing(1.5, 2),
      margin: theme.spacing(3, 2),
      fontSize: '0.85rem',
      lineHeight: 1.5,
      textAlign: 'left',
    },
    reviewButton: {
      fontWeight: 700,
      textTransform: 'none',
      borderRadius: 4,
      padding: theme.spacing(1, 2),
      borderColor: c.primary,
      color: c.primary,
      '&:hover': {
        borderColor: c.primaryHover,
        color: c.primaryHover,
      },
    },
    verifyAgainButton: {
      fontWeight: 700,
      textTransform: 'none',
      borderRadius: 4,
      padding: theme.spacing(1, 2),
      backgroundColor: c.buttonPrimaryBg,
      color: c.buttonPrimaryFg,
      '&:hover': {
        backgroundColor: c.primaryHover,
      },
    },
    actionsRow: {
      display: 'flex',
      justifyContent: 'space-between',
      width: '100%',
    },
  };
});

interface PlaygroundStepProps {
  labels: PlaygroundLabels;
  applicationDomain: string;
}

type PlaygroundState = 'initial' | 'loading' | 'success' | 'failed';

export const PlaygroundStep = ({ labels, applicationDomain }: PlaygroundStepProps) => {
  const classes = useStyles();
  const navigate = useNavigate();
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const alertApi = useApi(alertApiRef);
  const { session, completeSession, endSession } = useOnboardingContext();

  const [state, setState] = useState<PlaygroundState>('initial');

  const handleVerify = useCallback(async () => {
    setState('loading');
    try {
      const baseUrl = await discoveryApi.getBaseUrl('onboarding');
      const response = await fetchApi.fetch(`${baseUrl}/playground/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ applicationDomain }),
      });
      if (!response.ok) {
        const errorBody = await response.text();
        throw new Error(errorBody || `Request failed with status ${response.status}`);
      }
      const data = await response.json() as { exists: boolean; name: string | null };
      setState(data.exists ? 'success' : 'failed');
    } catch (error) {
      setState('failed');
      alertApi.post({
        message: error instanceof Error ? error.message : 'An unexpected error occurred',
        severity: 'error',
      });
    }
  }, [discoveryApi, fetchApi, applicationDomain, alertApi]);

  const handleContinue = useCallback(() => {
    const returnPath = session?.returnPath ?? '/onboarding';
    completeSession();
    navigate(returnPath);
  }, [session, completeSession, navigate]);

  const handleReviewJourney = useCallback(() => {
    const returnPath = session?.returnPath ?? '/onboarding';
    endSession();
    navigate(returnPath);
  }, [session, endSession, navigate]);

  // ── Success screen ──
  if (state === 'success') {
    return (
      <Box className={classes.container}>
        <CheckCircleOutlined className={classes.resultIconSuccess} />
        <Typography variant="h6" className={classes.resultTitle}>
          {labels.completed}
        </Typography>
        <Typography className={classes.resultMessage}>
          {labels.completedMessage}
          <br />
          {labels.nextJourneyUnlocked}
        </Typography>
        <Button
          variant="contained"
          className={classes.continueButton}
          onClick={handleContinue}
        >
          {labels.continueToJourney}
        </Button>
      </Box>
    );
  }

  // ── Failed screen ──
  if (state === 'failed') {
    return (
      <Box className={classes.container}>
        <WarningRounded className={classes.resultIconFail} />
        <Typography variant="h6" className={classes.resultTitle}>
          {labels.failedTitle}
        </Typography>
        <Typography className={classes.resultMessage}>
          {labels.failedMessage}
        </Typography>
        <a href={labels.tutorialUrl} target="_blank" rel="noopener noreferrer" className={classes.linkCard}>
          <DescriptionOutlined className={classes.linkCardIcon} />
          <Box>
            <Typography className={classes.linkCardTitle}>{labels.tutorialLabel}</Typography>
            <Typography className={classes.linkCardDescription}>{labels.tutorialDescription}</Typography>
          </Box>
        </a>
        <a href={labels.repoUrl} target="_blank" rel="noopener noreferrer" className={classes.linkCard}>
          <LinkOutlined className={classes.linkCardIcon} />
          <Box>
            <Typography className={classes.linkCardTitle}>{labels.repoLabel}</Typography>
            <Typography className={classes.linkCardDescription}>{labels.repoUrl}</Typography>
          </Box>
        </a>
        <Box className={classes.hintBox}>
          {labels.failedHint}
        </Box>
        <Box className={classes.actionsRow}>
          <Button
            variant="outlined"
            className={classes.reviewButton}
            onClick={handleReviewJourney}
          >
            {labels.reviewJourney}
          </Button>
          <Button
            variant="contained"
            className={classes.verifyAgainButton}
            onClick={handleVerify}
          >
            {labels.verifyAgain}
          </Button>
        </Box>
      </Box>
    );
  }

  // ── Initial / Loading screen ──
  return (
    <Box className={classes.container}>
      <AppsRounded className={classes.icon} />
      <Typography variant="h6" className={classes.title}>
        {labels.title}
      </Typography>
      <Typography className={classes.description}>
        {labels.description}
      </Typography>
      <Box className={classes.taskList}>
        <Typography className={classes.taskListHeader}>{labels.taskListHeader}</Typography>
        {labels.tasks.map((task, i) => (
          <Typography key={i} className={classes.taskItem}>
            {i + 1}. {task}
          </Typography>
        ))}
      </Box>
      <a href={labels.tutorialUrl} target="_blank" rel="noopener noreferrer" className={classes.linkCard}>
        <DescriptionOutlined className={classes.linkCardIcon} />
        <Box>
          <Typography className={classes.linkCardTitle}>{labels.tutorialLabel}</Typography>
          <Typography className={classes.linkCardDescription}>{labels.tutorialDescription}</Typography>
        </Box>
      </a>
      <a href={labels.repoUrl} target="_blank" rel="noopener noreferrer" className={classes.linkCard}>
        <LinkOutlined className={classes.linkCardIcon} />
        <Box>
          <Typography className={classes.linkCardTitle}>{labels.repoLabel}</Typography>
          <Typography className={classes.linkCardDescription}>{labels.repoUrl}</Typography>
        </Box>
      </a>
      <Typography className={classes.helperText}>
        {labels.verifyHint}
      </Typography>
      <Button
        variant="contained"
        className={classes.verifyButton}
        onClick={handleVerify}
        disabled={state === 'loading'}
      >
        {state === 'loading' ? (
          <CircularProgress size={22} color="inherit" />
        ) : (
          labels.verify
        )}
      </Button>
    </Box>
  );
};
